import { Component } from '@theme/component';

/** Fallback palette — the stop colours from the Figma spine design (node 283:3987). */
const DEFAULT_COLORS = ['#FF39FF', '#FFC930', '#FF47FF', '#D0F624', '#95FF00', '#50EFEC', '#16C4FE'];

/** Inclusive range of colour stops composed into each gradient. */
const STOP_COUNT_RANGE = [4, 6];

/**
 * Minimum distance between neighbouring stops, as a fraction of the viewport
 * height. At a full screen per segment, any single screenful of the strip
 * shows at most two colours — short pages drop stops to keep that true.
 */
const MIN_SEGMENT_SCREENS = 1;

/**
 * Maximum number of distinct palette colours drawn into a single gradient;
 * gradients with more stops repeat colours from this pool instead.
 */
const MAX_DISTINCT_COLORS = 4;

/**
 * Interpolating in OKLCH rotates hue at full chroma between stops, avoiding
 * the desaturated grey dead zones sRGB produces between opposing hues.
 */
const supportsOklch = CSS.supports('background', 'linear-gradient(in oklch, red, blue)');

/**
 * @typedef {object} SidebarGradientRefs
 * @property {HTMLElement} strip - The gradient strip element.
 */

/**
 * `<sidebar-gradient-component>` is the decorative Black Estate "spine" — the
 * gradient strip running down the page edge. On every page load it composes a
 * fresh vertical gradient: stop colours come from a pool of at most
 * `MAX_DISTINCT_COLORS` colours picked from the palette in `data-colors` —
 * each pool colour appears once before any extra stops draw randomly from
 * the pool (never the same colour twice in a row) — and stop offsets are
 * jittered around an even distribution, so spacing is random while the
 * colours stay spread across the strip's full height. The stop count is
 * capped by the page's height so segments never get shorter than
 * `MIN_SEGMENT_SCREENS` of the viewport — short pages get fewer colours
 * instead of a denser gradient.
 *
 * The component occupies the end column of the `.page-wrapper` grid (the
 * desktop scroll container) and stretches its strip to the wrapper's
 * scrollable height, so the strip spans the full page — not just the
 * viewport — and scrolls with the content.
 *
 * @extends {Component<SidebarGradientRefs>}
 */
class SidebarGradientComponent extends Component {
  requiredRefs = ['strip'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** Stop-count target for this page load; the page-height cap may lower it. */
  #targetStopCount =
    STOP_COUNT_RANGE[0] + Math.floor(Math.random() * (STOP_COUNT_RANGE[1] - STOP_COUNT_RANGE[0] + 1));

  /**
   * Randomised colour sequence and per-stop jitters in [-0.5, 0.5], extended
   * lazily so a gradient recomposed at a new stop count keeps its prefix.
   * @type {string[]}
   */
  #colors = [];

  /** @type {number[]} */
  #jitters = [];

  /**
   * Random pick of up to `MAX_DISTINCT_COLORS` palette colours — the only
   * colours this page load's gradients are composed from.
   * @type {string[] | undefined}
   */
  #pool;

  /** Stop count of the gradient currently applied to the strip. */
  #appliedStopCount = 0;

  /** Gradient awaiting promotion to the strip's base background after its fade-in. */
  #pendingGradient = '';

  /** @type {AbortController | undefined} */
  #listeners;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners = new AbortController();
    this.refs.strip.addEventListener('transitionend', this.#promoteGradient, {
      signal: this.#listeners.signal,
    });

    // Follow the page's content height via the wrapper's other children
    // (header group, main, footer) rather than the wrapper itself, so the
    // strip's own overflow never feeds back into the measurement.
    this.#resizeObserver = new ResizeObserver(this.#updateHeight);
    for (const sibling of this.parentElement?.children ?? []) {
      if (sibling !== this) this.#resizeObserver.observe(sibling);
    }
    this.#updateHeight();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#listeners?.abort();
  }

  /**
   * Stretches the strip to the scrollable height of the page. The strip's own
   * height is zeroed before measuring so it can shrink when content does.
   */
  #updateHeight = () => {
    const { strip } = this.refs;
    const wrapper = this.parentElement;
    if (!strip || !wrapper) return;

    strip.style.height = '0';
    const height = wrapper.scrollHeight;
    strip.style.height = `${height}px`;

    if (height > 0) this.#applyGradient(height);
  };

  /**
   * Composes the gradient at the stop count the page height allows and fades
   * the strip to it. Recomposes only when the stop count changes, so routine
   * resizes leave the gradient untouched.
   * @param {number} height - the strip's full height in px
   */
  #applyGradient(height) {
    const minSegment = window.innerHeight * MIN_SEGMENT_SCREENS;
    const cap = minSegment > 0 ? Math.floor(height / minSegment) + 1 : this.#targetStopCount;
    const count = Math.max(2, Math.min(this.#targetStopCount, cap));
    if (count === this.#appliedStopCount) return;

    this.#appliedStopCount = count;
    this.#fadeTo(this.#gradient(count, (minSegment / height) * 100));
  }

  /**
   * Fades the strip to a new gradient. `transition: background` can't
   * interpolate gradient images, so the gradient fades in on the strip's
   * `::after` overlay and is promoted to the base background once the
   * transition ends.
   * @param {string} gradient - the `linear-gradient()` value to fade in
   */
  #fadeTo(gradient) {
    const { strip } = this.refs;

    // Settle any in-flight fade first so the overlay restarts from opacity 0.
    // Promoting mid-fade snaps the remainder, but that only happens while the
    // page height is still settling during load.
    if (this.#pendingGradient) {
      this.#promoteGradient();
      void strip.offsetWidth;
    }

    this.#pendingGradient = gradient;
    strip.style.setProperty('--gradient-next', gradient);
    strip.classList.add('be-sidebar-gradient__strip--fading');
  }

  /**
   * Promotes the faded-in gradient to the strip's base background and resets
   * the overlay — an invisible swap, as both layers then show the same
   * gradient. Runs on the overlay's `transitionend` and directly when a new
   * fade needs to settle the previous one.
   * @param {TransitionEvent} [event]
   */
  #promoteGradient = (event) => {
    if (event && event.pseudoElement !== '::after') return;
    if (!this.#pendingGradient) return;

    this.refs.strip.style.backgroundImage = this.#pendingGradient;
    this.refs.strip.classList.remove('be-sidebar-gradient__strip--fading');
    this.#pendingGradient = '';
  };

  /** @returns {string[]} the palette from `data-colors`, falling back to the design palette */
  get #palette() {
    const colors = (this.dataset.colors ?? '')
      .split(',')
      .map((color) => color.trim())
      .filter(Boolean);

    return colors.length > 1 ? colors : DEFAULT_COLORS;
  }

  /** @returns {string[]} up to `MAX_DISTINCT_COLORS` colours drawn at random from the palette */
  #randomPool() {
    const palette = [...this.#palette];
    /** @type {string[]} */
    const pool = [];
    while (pool.length < MAX_DISTINCT_COLORS && palette.length > 0) {
      pool.push(...palette.splice(Math.floor(Math.random() * palette.length), 1));
    }
    return pool;
  }

  /**
   * @param {number} count - number of colour stops
   * @param {number} minSegmentPct - minimum segment length as a % of the strip
   * @returns {string} a randomised `linear-gradient()` value
   */
  #gradient(count, minSegmentPct) {
    const pool = (this.#pool ??= this.#randomPool());

    while (this.#colors.length < count) {
      const index = this.#colors.length;
      // The first stops walk the shuffled pool so every colour appears once;
      // once the pool is spent, stops keep drawing from it at random — no
      // repeated cycle, and no two neighbouring stops alike.
      const choices =
        index < pool.length ? [pool[index]] : pool.filter((color) => color !== this.#colors[index - 1]);
      this.#colors.push(/** @type {string} */(choices[Math.floor(Math.random() * choices.length)] ?? pool[0]));
      this.#jitters.push(Math.random() - 0.5);
    }

    // Even spacing jittered by up to ±40% of a segment, but never so far that
    // a segment shrinks below the minimum: offsets stay monotonic (no
    // crossing stops) yet cover the whole strip end to end.
    const segment = 100 / (count - 1);
    const amplitude = Math.max(0, Math.min(segment * 0.4, (segment - minSegmentPct) / 2));
    const stops = this.#colors.slice(0, count).map((color, index) => {
      const jitter = index === 0 || index === count - 1 ? 0 : this.#jitters[index] * 2 * amplitude;
      return `${color} ${(index * segment + jitter).toFixed(2)}%`;
    });

    return `linear-gradient(${supportsOklch ? '180deg in oklch' : '180deg'}, ${stops.join(', ')})`;
  }
}

if (!customElements.get('sidebar-gradient-component')) {
  customElements.define('sidebar-gradient-component', SidebarGradientComponent);
}
